import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@arcadeai/design-system/components/ui/atoms/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { ChevronDown } from "lucide-react";
import type { ReactNode } from "react";
import type { Binding } from "@/app/types";
import { getBindingInfo } from "@/lib/binding";

type CollapsibleResourceSectionProps = {
  binding: Binding;
  itemsCount: number;
  resourceType: string;
  defaultOpen?: boolean;
  overriddenCount?: number;
  overriddenTooltipContent?: string;
  children?: ReactNode;
};

export const CollapsibleResourceSection = ({
  binding,
  itemsCount,
  resourceType,
  defaultOpen = true,
  overriddenCount,
  overriddenTooltipContent,
  children,
}: CollapsibleResourceSectionProps) => {
  const bindingInfo = getBindingInfo(binding);
  const { icon: Icon, classes, description } = bindingInfo;
  const title = `${bindingInfo.displayName} ${resourceType}`;
  const emptyMessage = `No ${binding} ${resourceType} found.`;

  return (
    <Collapsible className="w-full rounded-lg border" defaultOpen={defaultOpen}>
      <CollapsibleTrigger
        render={
          <button
            className="group flex w-full items-center gap-3 rounded-t-lg px-4 py-3 transition-colors hover:bg-muted/50"
            type="button"
          />
        }
      >
        <div
          className={`flex size-10 items-center justify-center rounded-md ${classes.sectionBackground}`}
        >
          <Icon className={`size-5 ${classes.sectionIcon}`} />
        </div>
        <div className="flex flex-1 flex-col">
          <div className="flex items-center gap-2">
            <h2 className={`font-semibold text-base ${classes.sectionTitle}`}>
              {title}
            </h2>
            <span className="text-muted-foreground text-sm">
              ({itemsCount})
            </span>
          </div>
          <p className="text-left text-muted-foreground text-xs leading-relaxed">
            {description}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(overriddenCount ?? 0) > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Badge
                      className="bg-warning/80 text-warning-foreground"
                      variant="warning"
                    />
                  }
                >
                  <span className="px-1 py-0.5 font-medium text-xs">
                    <span className="font-bold">{overriddenCount}</span>{" "}
                    overridden
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{overriddenTooltipContent}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <ChevronDown className="size-4 -rotate-90 text-muted-foreground transition-transform group-data-[panel-open]:rotate-0" />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent className="border-t p-4">
        {children ? (
          <div>{children}</div>
        ) : (
          <div className="rounded-lg border border-dashed py-8 text-center text-muted-foreground">
            <Icon
              className={`mx-auto mb-2 h-8 w-8 ${classes.sectionEmptyIcon}`}
            />
            <p>{emptyMessage}</p>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};
